import { calendarCallColor } from "@shared/call-participant-tone";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { formatInTimeZone } from "date-fns-tz";
import {
  CalendarDays,
  CalendarPlus,
  History,
  Loader2,
  Phone,
  PhoneCall,
  Settings2,
  Users,
  Video,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useCall } from "@/contexts/call-context";
import { useAuth } from "@/contexts/auth-context";
import { queryClient } from "@/lib/queryClient";
import { ProjectCommunicationActions } from "@/components/project-communication-actions";
import {
  type CalendarCallAction,
  type CalendarItem,
  formatCalendarCallCountdown,
  isCalendarCountdownAbsolute,
  isUpcomingCalendarCall,
  isPriorityCalendarCall,
  useCalendarAgenda,
  useCalendarCallActions,
  useCalendarClock,
  useCalendarHistory,
  useCalendarOptions,
  useCalendarRefreshEvents,
  usePatchCallAction,
  invalidateCalendar,
  type ScheduledView,
} from "@/hooks/use-calendar";
import { useOpenProjectLikeQuickSearch } from "@/lib/quick-search/open-project";
import { CalendarCallDialog } from "./CalendarCallDialog";
import { CalendarEventDialog } from "./CalendarEventDialog";
import { CalendarSubscriptionsDialog } from "./CalendarSubscriptionsDialog";
import { CallbackRequestTopic } from "./CallbackRequestTopic";

export function calendarAgendaRange(now = new Date()) {
  const from = new Date(now);
  from.setDate(from.getDate() - 1);
  const to = new Date(now);
  to.setDate(to.getDate() + 90);
  return {
    from: formatInTimeZone(from, "Europe/Berlin", "yyyy-MM-dd"),
    to: formatInTimeZone(to, "Europe/Berlin", "yyyy-MM-dd"),
  };
}

function isPendingRequest(action: CalendarCallAction) {
  return action.status === "pending" || action.status === "requested";
}

function requestLabel(action: CalendarCallAction, userId?: number) {
  // Callback requests are always the signed-in user's actions, even when an
  // admin is viewing another user's schedule.
  const person = action.requesterUserId === userId && userId != null
    ? action.recipientName || "Team member"
    : action.requesterName || action.recipientName || "Team member";
  return `${person} · ${action.callType === "video" ? "video" : "audio"} callback`;
}

function itemDate(item: CalendarItem) {
  if (item.allDay) {
    const date = new Date(`${item.startAt.slice(0, 10)}T12:00:00`);
    return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }
  return new Date(item.startAt).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ItemIcon({ item }: { item: CalendarItem }) {
  if (item.kind === "call" || item.kind === "call_history") {
    const style = { color: calendarCallColor(item) };
    if ((item.attendees?.length ?? 0) > 2) return <Users className="h-3.5 w-3.5" style={style} />;
    return item.callType === "audio"
      ? <Phone className="h-3.5 w-3.5" style={style} />
      : <Video className="h-3.5 w-3.5" style={style} />;
  }
  if (item.kind === "deadline") return <CalendarDays className="h-3.5 w-3.5" style={{ color: item.color || undefined }} />;
  return <CalendarDays className="h-3.5 w-3.5 text-gray-400" />;
}

export function CalendarCallCountdown({ item, now }: { item: CalendarItem; now: number }) {
  const countdown = formatCalendarCallCountdown(item.startAt, now);
  const absolute = isCalendarCountdownAbsolute(item.startAt, now);
  const description = countdown === "Due"
    ? "Call is due now"
    : absolute ? `Call starts ${countdown}` : `Call starts in ${countdown}`;
  return (
    <span
      className={`shrink-0 min-w-[4.75rem] max-w-[6.5rem] overflow-hidden whitespace-nowrap text-right font-semibold tabular-nums leading-none ${absolute ? "text-xs" : "text-sm"}`}
      style={{ color: calendarCallColor(item) }}
      aria-label={description}
      data-testid={`calendar-call-countdown-${item.id}`}
      title={description}
    >
      {countdown}
    </span>
  );
}

interface ScheduledCallRowProps {
  item: CalendarItem;
  now: number;
  compact?: boolean;
  onSelect: (item: CalendarItem) => void;
}

export function ScheduledCallRow({ item, now, compact, onSelect }: ScheduledCallRowProps) {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-2 rounded px-1.5 py-1 border-l-2 border-l-transparent hover:bg-gray-700/60 text-left min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      style={{ borderLeftColor: calendarCallColor(item) }}
      onClick={() => onSelect(item)}
      data-testid={`calendar-item-${item.id}`}
    >
      {item.projectThumbnail ? <img src={item.projectThumbnail} alt={`${item.projectName || item.projectId || "Project"} thumbnail`} className="h-6 w-10 rounded object-cover border border-gray-600 flex-shrink-0" /> : <ItemIcon item={item} />}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-gray-200 truncate flex items-center gap-1">
          {item.projectThumbnail && <ItemIcon item={item} />}
          <span className="truncate">{item.title}</span>
        </div>
        {!compact && <div className="text-[10px] text-gray-400 truncate">{itemDate(item)}{item.projectName ? ` · ${item.projectId} ${item.projectName}` : ""}</div>}
      </div>
      {item.busy && !compact && <span className="shrink-0 text-[9px] text-gray-500 uppercase">Busy</span>}
      <CalendarCallCountdown item={item} now={now} />
    </button>
  );
}

function ScheduledProjectDateRow({
  item,
  onSelect,
  readOnly = false,
}: {
  item: CalendarItem;
  onSelect: (item: CalendarItem) => void;
  readOnly?: boolean;
}) {
  const openProject = useOpenProjectLikeQuickSearch();
  // Project timings (due date, deadline, tagged dates, pulse due dates) are
  // read-only projections: clicking one jumps to the project in the tree,
  // exactly like a quick-search hit. Other rows still open their detail dialog.
  const isProjectTiming = item.sourceType === "project_date" && !!item.projectId;
  const handleClick = () => {
    if (isProjectTiming) {
      openProject(item.projectId!);
      return;
    }
    onSelect(item);
  };
  return (
    <div
      className="w-full flex items-center gap-2 rounded px-1.5 py-1 border-l-2 border-l-transparent hover:bg-gray-700/60 min-w-0"
      style={item.color ? { borderLeftColor: item.color } : undefined}
    >
      <button
        type="button"
        className="flex-1 min-w-0 flex items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        onClick={handleClick}
        title={isProjectTiming ? `Open project ${item.projectId}` : undefined}
        data-testid={`calendar-item-${item.id}`}
      >
        {item.projectThumbnail ? <img src={item.projectThumbnail} alt="" className="h-6 w-10 rounded object-cover border border-gray-600 flex-shrink-0" /> : <ItemIcon item={item} />}
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-200 truncate flex items-center gap-1">
            {item.projectThumbnail && <ItemIcon item={item} />}
            <span className="truncate">{item.title}</span>
          </div>
          <div className="text-[10px] text-gray-400 truncate">{itemDate(item)}{item.projectName ? ` · ${item.projectId} ${item.projectName}` : ""}</div>
        </div>
        {item.busy && <span className="shrink-0 text-[9px] text-gray-500 uppercase">Busy</span>}
      </button>
      {!readOnly && item.sourceType === "project_date" && item.projectId && (
        <ProjectCommunicationActions
          projectId={item.projectId}
          projectName={item.projectName}
          className="shrink-0"
        />
      )}
    </div>
  );
}

function eventStub(id: string, readOnly = false): CalendarItem {
  return {
    id,
    sourceType: "event",
    sourceId: id,
    kind: "event",
    title: "Calendar entry",
    startAt: new Date().toISOString(),
    endAt: new Date().toISOString(),
    allDay: false,
    timezone: null,
    projectId: null,
    projectName: null,
    projectThumbnail: null,
    ownerUserId: null,
    busy: false,
    color: null,
    version: null,
    canEdit: false,
    canCancel: false,
    attendees: null,
    pulseLinks: [],
    ...(readOnly ? { readOnly: true } : {}),
  };
}

function openPulseLink(pulseId: number, projectId: string | null) {
  if (!projectId) {
    window.dispatchEvent(new CustomEvent("open-pulse-workbench", { detail: { pulseId } }));
    return;
  }
  const params = new URLSearchParams({
    calendarProject: projectId,
    calendarPulse: String(pulseId),
  });
  window.history.pushState(window.history.state, "", `/?${params.toString()}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

interface RequestRowProps {
  action: CalendarCallAction;
  compact?: boolean;
}

function RequestRow({ action, compact }: RequestRowProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const call = useCall() as ReturnType<typeof useCall> & {
    startCalendarCallback?: (actionId: string) => Promise<void>;
  };
  const dismiss = usePatchCallAction("dismiss");
  const cancel = usePatchCallAction("cancel");
  const canStart = action.canStart ?? action.recipientUserId === user?.id;
  const canDismiss = action.canDismiss ?? action.recipientUserId === user?.id;
  const canCancel = action.canCancel ?? action.requesterUserId === user?.id;

  const start = async () => {
    if (!call.startCalendarCallback) return;
    try {
      await call.startCalendarCallback(action.id);
      await invalidateCalendar(queryClient);
    } catch (error: any) {
      toast({ title: "Could not start callback", description: error?.message, variant: "destructive" });
    }
  };

  return (
    <div className="flex items-center gap-2 rounded px-1.5 py-1 bg-amber-500/10 border border-amber-500/25 min-w-0" data-testid={`calendar-request-${action.id}`}>
      {action.projectThumbnail ? (
        <img src={action.projectThumbnail} alt={`${action.projectName || action.projectId || "Project"} thumbnail`} className="w-10 h-6 rounded object-cover border border-gray-600 flex-shrink-0" />
      ) : action.callType === "video" ? (
        <Video className="h-4 w-4 text-amber-300 flex-shrink-0" />
      ) : (
        <PhoneCall className="h-4 w-4 text-amber-300 flex-shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-gray-100 truncate flex items-center gap-1">
          {action.projectThumbnail && (action.callType === "video"
            ? <Video className="h-3.5 w-3.5 text-amber-300 shrink-0" />
            : <PhoneCall className="h-3.5 w-3.5 text-amber-300 shrink-0" />)}
          <span className="truncate">{requestLabel(action, user?.id)}</span>
        </div>
        {!compact && <div className="text-[10px] text-gray-400 truncate">{action.projectName || "No project"} · {new Date(action.createdAt).toLocaleString()}</div>}
        <CallbackRequestTopic action={action} userId={user?.id} compact={compact} />
      </div>
      {canStart && (
        <Button type="button" size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-500" onClick={start} disabled={!call.startCalendarCallback}>Call</Button>
      )}
      {canDismiss && (
        <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-gray-400" aria-label="Dismiss callback request" onClick={() => dismiss.mutate(action.id)} disabled={dismiss.isPending}><X className="h-3 w-3" /></Button>
      )}
      {canCancel && (
        <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-red-300" onClick={() => cancel.mutate(action.id)} disabled={cancel.isPending}>Cancel</Button>
      )}
    </div>
  );
}

export function CalendarUrgentList({
  actions,
  scheduledCalls = [],
  now = Date.now(),
  onSelectCalendarItem = () => undefined,
}: {
  actions: CalendarCallAction[];
  scheduledCalls?: CalendarItem[];
  now?: number;
  onSelectCalendarItem?: (item: CalendarItem) => void;
}) {
  const requests = actions.filter(isPendingRequest).sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  if (requests.length === 0 && scheduledCalls.length === 0) return null;
  return (
    <>
      {scheduledCalls.length > 0 && (
        <div className="space-y-0.5">
          <div className="px-1.5 pb-1 text-[10px] uppercase tracking-wide text-sky-300">Calls — next 2 days</div>
          {[...scheduledCalls].sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt)).map((item) => <ScheduledCallRow key={`${item.sourceType}:${item.id}`} item={item} now={now} compact onSelect={onSelectCalendarItem} />)}
        </div>
      )}
      {requests.length > 0 && (
        <>
          <div className="px-1.5 pb-1 text-[10px] uppercase tracking-wide text-amber-300">Callback requests</div>
          <div className="space-y-1">{requests.map((action) => <RequestRow key={action.id} action={action} compact />)}</div>
        </>
      )}
    </>
  );
}

const noop = () => undefined;

/**
 * Admin-only row of team short codes. Selecting one switches the Scheduled
 * list to that user's perspective; All combines those team lists with the
 * signed-in admin's own schedule. Selecting the active chip again returns to
 * the admin's own view.
 */
export function ScheduledViewAsChips({
  value,
  onChange,
  fallback = null,
}: {
  value: ScheduledView;
  onChange: (userId: ScheduledView) => void;
  /** Rendered for non-admins (and while the admin check is still loading). */
  fallback?: ReactNode;
}) {
  const options = useCalendarOptions();
  if (!options.data?.isAdmin) return <>{fallback}</>;
  const others = options.data.users.filter((entry) => entry.role !== "admin");
  if (others.length === 0) return <>{fallback}</>;
  return (
    <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="View Scheduled as">
      <button
        type="button"
        role="radio"
        aria-checked={value === "all"}
        title={value === "all" ? "Viewing your schedule and all team schedules — click to return to your view" : "View your schedule and all team schedules"}
        className={`h-5 rounded px-1.5 text-[10px] font-medium uppercase tracking-wide border transition-colors ${
          value === "all"
            ? "bg-amber-500/20 border-amber-400/60 text-amber-200"
            : "border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500"
        }`}
        onClick={() => onChange(value === "all" ? null : "all")}
        data-testid="scheduled-view-as-all"
      >
        All
      </button>
      {others.map((entry) => {
        const active = value === entry.id;
        return (
          <button
            key={entry.id}
            type="button"
            role="radio"
            aria-checked={active}
            title={active ? `Viewing as ${entry.name} — click to return to your view` : `View Scheduled as ${entry.name}`}
            className={`h-5 rounded px-1.5 text-[10px] font-medium uppercase tracking-wide border transition-colors ${
              active
                ? "bg-amber-500/20 border-amber-400/60 text-amber-200"
                : "border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-500"
            }`}
            onClick={() => onChange(active ? null : entry.id)}
            data-testid={`scheduled-view-as-${entry.id}`}
          >
            {entry.shortCode || entry.name.slice(0, 3)}
          </button>
        );
      })}
    </div>
  );
}

export function ScheduledCalendarPanel({
  initialEventId = null,
  onInitialEventConsumed = noop,
  viewAsUserId = null,
}: {
  /** Admin-only: read the agenda from another team member's perspective. */
  viewAsUserId?: ScheduledView;
  /** One-shot navigation target supplied by the activity bar. */
  initialEventId?: string | null;
  /** Clears the parent navigation target after this panel has handled it. */
  onInitialEventConsumed?: () => void;
}) {
  useCalendarRefreshEvents();
  const range = useMemo(calendarAgendaRange, []);
  const agenda = useCalendarAgenda(range.from, range.to, viewAsUserId);
  const viewingOther = viewAsUserId != null;
  const viewingAll = viewAsUserId === "all";
  const actions = useCalendarCallActions();
  const now = useCalendarClock();
  const [tab, setTab] = useState<"agenda" | "history">("agenda");
  const activeTab = viewingAll ? "agenda" : tab;
  const history = useCalendarHistory(activeTab === "history");
  const [newEvent, setNewEvent] = useState(false);
  const [newCall, setNewCall] = useState(false);
  const [subscriptions, setSubscriptions] = useState(false);
  const [selected, setSelected] = useState<CalendarItem | null>(null);

  useEffect(() => {
    if (viewingAll) {
      setNewEvent(false);
      setNewCall(false);
      setSubscriptions(false);
      setSelected((current) => current && !current.readOnly ? { ...current, readOnly: true } : current);
    }
  }, [viewingAll]);

  useEffect(() => {
    if (!initialEventId) return;
    setSelected(eventStub(initialEventId, viewAsUserId === "all"));
    onInitialEventConsumed();
    // Deliberately do not depend on agenda data: this is a one-shot
    // navigation target, not a selected row to be restored after refetching.
  }, [initialEventId, onInitialEventConsumed, viewAsUserId]);

  useEffect(() => {
    const openEvent = (event: Event) => {
      const id = String((event as CustomEvent<{ eventId?: string }>).detail?.eventId || "");
      if (!id) return;
      // A custom event is likewise a fresh navigation request. Use the
      // detail query as the source of truth instead of retaining an agenda
      // snapshot which may already be terminal.
      setSelected(eventStub(id, viewAsUserId === "all"));
    };
    window.addEventListener("open-calendar-event", openEvent);
    return () => window.removeEventListener("open-calendar-event", openEvent);
  }, [viewAsUserId]);

  const requests = (actions.data?.items ?? []).filter(isPendingRequest)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  const upcoming = (agenda.data?.items ?? [])
    .filter((item) => item.sourceType !== "call_history"
      && item.sourceType !== "bank_holiday"
      && item.endAt.slice(0, 10) >= new Date(now).toISOString().slice(0, 10)
      && (item.kind !== "call" || isUpcomingCalendarCall(item, now)))
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt));
  const historyItems = history.data?.pages.flatMap((page) => page.items) ?? [];
  const priorityCalls = upcoming.filter((item) => isPriorityCalendarCall(item, now));
  const remainingUpcoming = upcoming.filter((item) => !isPriorityCalendarCall(item, now));

  return (
    <div className="space-y-2 min-w-0" data-testid="scheduled-calendar-panel">
      <div className="flex items-center gap-1 border-b border-gray-700 pb-1.5">
        {!viewingAll && (
          <>
            <Button type="button" size="sm" className="h-7 px-2 text-[11px]" onClick={() => setNewCall(true)}><PhoneCall className="h-3 w-3 mr-1" />+ Call</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] border-gray-600" onClick={() => setNewEvent(true)}><CalendarPlus className="h-3 w-3 mr-1" />+ Event</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px] ml-auto" onClick={() => setSubscriptions(true)}><Settings2 className="h-3 w-3 mr-1" />Phone</Button>
          </>
        )}
      </div>

      <div className="flex gap-1" role="tablist" aria-label="Calendar views">
        <Button type="button" size="sm" variant={activeTab === "agenda" ? "secondary" : "ghost"} className="h-6 px-2 text-[10px]" role="tab" aria-selected={activeTab === "agenda"} onClick={() => setTab("agenda")}>Agenda</Button>
        {!viewingAll && (
          <Button type="button" size="sm" variant={tab === "history" ? "secondary" : "ghost"} className="h-6 px-2 text-[10px]" role="tab" aria-selected={tab === "history"} onClick={() => setTab("history")}><History className="h-3 w-3 mr-1" />Call history</Button>
        )}
      </div>

      {activeTab === "agenda" ? (
        <>
          {priorityCalls.length > 0 && (
            <section aria-label="Calls in the next 2 days" className="space-y-0.5">
              <div className="text-[10px] uppercase tracking-wide text-sky-300 px-1">Calls — next 2 days</div>
              {priorityCalls.map((item) => <ScheduledCallRow key={`${item.sourceType}:${item.id}`} item={item} now={now} onSelect={setSelected} />)}
            </section>
          )}
          {!viewingOther && actions.isError && (
            <div role="alert" className="rounded border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-200">
              Callback requests could not be loaded.
            </div>
          )}
          {!viewingOther && requests.length > 0 && (
            <section aria-label="Callback requests" className="space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-amber-300 px-1">Callback requests</div>
              {requests.map((action) => <RequestRow key={action.id} action={action} />)}
            </section>
          )}
          <section aria-label="Upcoming calendar entries" className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 px-1 pt-1">
              {viewingAll ? "Upcoming — all team schedules" : viewingOther ? "Upcoming — viewing another user's list" : "Upcoming"}
            </div>
            {agenda.isLoading ? (
              <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /></div>
            ) : agenda.isError ? (
              <div role="alert" className="text-xs text-red-300 p-2">The calendar agenda could not be loaded.</div>
            ) : upcoming.length === 0 ? (
              <div className="text-xs text-gray-500 p-2">Nothing scheduled in the next 90 days.</div>
            ) : remainingUpcoming.map((item) => item.kind === "call" ? (
              <ScheduledCallRow key={`${item.sourceType}:${item.id}`} item={item} now={now} onSelect={setSelected} />
            ) : (
              <ScheduledProjectDateRow key={`${item.sourceType}:${item.id}`} item={item} onSelect={setSelected} readOnly={viewingAll} />
            ))}
            {agenda.data?.truncated && <div className="text-[10px] text-amber-300 px-1 pt-1">Showing the bounded agenda limit.</div>}
          </section>
        </>
      ) : (
        <section aria-label="Call history" className="space-y-0.5">
          {viewingOther && (
            <div className="text-[10px] text-amber-300 px-1 pt-1">Call history always shows your own calls, not the selected user's.</div>
          )}
          {history.isLoading ? (
            <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" /></div>
          ) : history.isError ? (
            <div role="alert" className="text-xs text-red-300 p-2">Call history could not be loaded.</div>
          ) : historyItems.length === 0 ? (
            <div className="text-xs text-gray-500 p-2">No call history.</div>
          ) : historyItems.map((item) => (
            <div key={item.id} className="flex items-center gap-2 rounded px-1.5 py-1 opacity-70">
              <ItemIcon item={item} />
              <div className="min-w-0 flex-1">
                <div className="text-xs truncate">{item.title}</div>
                <div className="text-[10px] text-gray-400">{itemDate(item)}</div>
              </div>
              {item.pulseLinks.map((link) => (
                <Button
                  key={link.pulseId}
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-[10px] text-blue-300"
                  onClick={() => openPulseLink(link.pulseId, link.projectId)}
                >
                  Notes
                </Button>
              ))}
            </div>
          ))}
          {history.hasNextPage && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-full text-xs"
              disabled={history.isFetchingNextPage}
              onClick={() => history.fetchNextPage()}
            >
              {history.isFetchingNextPage && <Loader2 className="h-3 w-3 mr-1 animate-spin motion-reduce:animate-none" />}
              Load older calls
            </Button>
          )}
        </section>
      )}

      <CalendarEventDialog open={newEvent} onOpenChange={setNewEvent} />
      <CalendarCallDialog open={newCall} onOpenChange={setNewCall} />
      <CalendarSubscriptionsDialog open={subscriptions} onOpenChange={setSubscriptions} />
      <CalendarEventDialog open={!!selected} onOpenChange={(next) => !next && setSelected(null)} item={selected} />
    </div>
  );
}